import React, { useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSessionContext } from '../context/SessionContext';
import { useGamificationContext } from '../context/GamificationContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import Button from '../components/ui/Button';
import { FiArrowLeft, FiEdit, FiTrash2, FiTrendingUp, FiTarget, FiClock, FiCalendar, FiCheckCircle, FiXCircle, FiAlertCircle, FiActivity, FiStar } from 'react-icons/fi';
import { formatDate, getPositionColor } from '../lib/utils';
import { techniques as allTechniquesBase } from '../data/techniques';

const TechniqueDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { sessions } = useSessionContext();
  const { calculateTechniqueMastery } = useGamificationContext();

  const technique = useMemo(() => {
    const baseTechnique = allTechniquesBase.find((t) => t.id === parseInt(id));

    if (!baseTechnique) return null;

    // Get basic stats
    const techniqueSessions = [];
    sessions.forEach((session) => {
      if (session.techniques) {
        session.techniques.forEach((tech) => {
          if (tech.name.toLowerCase() === baseTechnique.name.toLowerCase()) {
            techniqueSessions.push({
              sessionId: session.id,
              date: session.date,
              outcome: tech.outcome,
              partner: session.partner || 'Training Partner',
              notes: session.notes || 'No notes recorded'
            });
          }
        });
      }
    });

    const sortedSessions = [...techniqueSessions].sort(
      (a, b) => new Date(b.date) - new Date(a.date)
    );
    const lastUsed = sortedSessions.length > 0 ? sortedSessions[0].date : null;

    // Get Gamified Mastery Stats
    const mastery = calculateTechniqueMastery(baseTechnique.name);

    return {
      ...baseTechnique,
      ...mastery, // level, title, progress, count, successRate
      lastUsed,
      recentSessions: sortedSessions.slice(0, 5)
    };
  }, [id, sessions, calculateTechniqueMastery]);

  if (!technique) return null; // Or loading/error state

  const getDifficultyColor = (difficulty) => {
    const colors = {
      Beginner: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      Intermediate: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      Advanced: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
    };
    return colors[difficulty] || 'bg-gray-100 text-gray-700 dark:bg-slate-800 dark:text-slate-300';
  };

  const getOutcomeIcon = (outcome) => {
    switch (outcome) {
      case 'Succeeded':
        return <FiCheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />;
      case 'Failed':
        return <FiXCircle className="w-4 h-4 text-red-600 dark:text-red-400" />;
      case 'Attempted':
        return <FiAlertCircle className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />;
      default:
        return <FiTarget className="w-4 h-4 text-gray-600 dark:text-slate-400" />;
    }
  };

  const getMasteryColor = (level) => {
    switch(level) {
      case 4: return 'border-slate-900 bg-slate-900 text-white dark:border-slate-700 dark:bg-slate-800'; 
      case 3: return 'border-amber-800 bg-amber-800 text-white dark:border-amber-700 dark:bg-amber-900 dark:text-amber-100'; 
      case 2: return 'border-purple-700 bg-purple-700 text-white dark:border-purple-600 dark:bg-purple-900 dark:text-purple-100'; 
      case 1: return 'border-blue-600 bg-blue-600 text-white dark:border-blue-600 dark:bg-blue-900 dark:text-blue-100'; 
      default: return 'border-slate-200 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300'; 
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <Link to="/techniques">
          <Button variant="ghost" size="sm" className="mb-4">
            <FiArrowLeft className="w-4 h-4 mr-2" /> Back to Techniques
          </Button>
        </Link>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">
                {technique.name}
              </h1>
              <span
                className={`px-2 py-1 text-xs rounded-full ${getDifficultyColor(
                  technique.difficulty
                )}`}
              >
                {technique.difficulty}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600 dark:text-slate-400">
              <span className="flex items-center">
                <span
                  className="w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: getPositionColor(technique.position) }}
                />
                {technique.position}
              </span>
              <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                {technique.type}
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            <Link to={`/techniques/${id}/edit`}>
              <Button variant="outline" size="sm">
                <FiEdit className="w-4 h-4 mr-2" /> Edit
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Gamification / Mastery Card */}
      <Card className="mb-8 border-2 border-blue-100 dark:border-blue-900 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-50 to-white dark:from-blue-900/20 dark:to-slate-800/50 p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            {/* Badge */}
            <div className={`w-24 h-24 rounded-full flex flex-col items-center justify-center border-4 shadow-sm ${getMasteryColor(technique.level)}`}>
              <FiStar className={`w-8 h-8 mb-1 ${technique.level > 0 ? 'fill-current' : ''}`} />
              <span className="text-xs font-bold uppercase tracking-wider">Level {technique.level}</span>
            </div>
            
            {/* Progress Info */}
            <div className="flex-1 w-full">
              <div className="flex justify-between items-end mb-2">
                <div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white">{technique.title} Rank</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {technique.level === 4 
                      ? 'Max Level Reached!' 
                      : `Next Rank: ${['Apprentice', 'Practitioner', 'Expert', 'Specialist'][technique.level]}`}
                  </p>
                </div>
                <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">{Math.round(technique.progress)}%</span>
              </div>
              
              <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-4 overflow-hidden mb-2">
                <div 
                  className="bg-blue-600 h-full rounded-full transition-all duration-500"
                  style={{ width: `${technique.progress}%` }}
                />
              </div>
              
              <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400">
                 <span>Current XP: {technique.count * 20}</span>
                 <span>Requirements: Uses & Success Rate</span>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Standard Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">Success Rate</p>
              <FiTrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 dark:text-green-400" />
            </div>
            <p className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
              {Math.round(technique.successRate)}%
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">Times Used</p>
              <FiTarget className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <p className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
              {technique.count}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">Last Used</p>
              <FiClock className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <p className="text-sm sm:text-base font-bold text-slate-900 dark:text-white">
              {technique.lastUsed ? formatDate(technique.lastUsed) : 'Never'}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 mb-6 sm:mb-8">
        {/* Description */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base sm:text-lg dark:text-white">Description</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed">
              {technique.description}
            </p>
          </CardContent>
        </Card>

        {/* Key Points */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base sm:text-lg dark:text-white">Key Points</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {technique.keyPoints.map((point, index) => (
                <li key={index} className="flex items-start text-sm sm:text-base">
                  <FiCheckCircle className="w-4 h-4 text-green-600 dark:text-green-500 mr-2 mt-1 flex-shrink-0" />
                  <span className="text-slate-700 dark:text-slate-300">{point}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Common Mistakes */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base sm:text-lg dark:text-white">Common Mistakes</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
              {technique.commonMistakes.map((mistake, index) => (
                <li key={index} className="flex items-start text-sm sm:text-base">
                  <FiXCircle className="w-4 h-4 text-red-600 dark:text-red-500 mr-2 mt-1 flex-shrink-0" />
                  <span className="text-slate-700 dark:text-slate-300">{mistake}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Recent Sessions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base sm:text-lg dark:text-white">Recent Sessions</CardTitle>
        </CardHeader>
        <CardContent>
          {technique.recentSessions.length > 0 ? (
            <div className="space-y-3">
              {technique.recentSessions.map((session, idx) => (
                <div
                  key={idx}
                  className="p-4 border rounded-lg bg-slate-50 border-slate-200 dark:bg-slate-800 dark:border-slate-700"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        {getOutcomeIcon(session.outcome)}
                        <span className="font-medium text-slate-900 dark:text-white text-sm sm:text-base">
                          {session.outcome}
                        </span>
                        <span className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                          vs {session.partner}
                        </span>
                      </div>
                      <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 break-words">
                        {session.notes}
                      </p>
                    </div>
                    <div className="flex items-center text-xs sm:text-sm text-slate-500 dark:text-slate-400 flex-shrink-0">
                      <FiCalendar className="w-3 h-3 mr-1" />
                      {formatDate(session.date)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500 dark:text-slate-400">
              <FiActivity className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm sm:text-base">No sessions recorded yet</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TechniqueDetail;